import type { WorkspaceData } from "@/shared/contracts";
import { type MessagingChannel, isMessagingChannel, textForLead } from "@repo/polaris-shared";

/**
 * Polaris sends only through an active WhatsApp connection with sends enabled.
 * Otherwise (e.g. messages imported from WhatsApp Desktop, before Coexistence)
 * the operator sends from their own WhatsApp and the import brings it back.
 */
export function canSendWhatsapp(workspace: Pick<WorkspaceData, "config" | "connections">) {
  return (
    workspace.config.sendsEnabled &&
    workspace.connections.some(
      (connection) => connection.kind === "whatsapp" && connection.status === "active",
    )
  );
}

/**
 * The operator can write from their own WhatsApp: Polaris cannot send yet, or
 * the number is also on the WhatsApp Business app (Coexistence), where there is
 * no 24-hour window and the message comes back to Polaris as an echo.
 */
export function phoneAppAvailable(workspace: Pick<WorkspaceData, "config" | "connections">) {
  return (
    !canSendWhatsapp(workspace) ||
    workspace.connections.some(
      (connection) =>
        connection.kind === "whatsapp" &&
        connection.status === "active" &&
        connection.onBusinessApp,
    )
  );
}

/**
 * The number «Apri su WhatsApp» writes to: the lead's current chat when there is
 * one (the API prefers a chat where the contact wrote), else the lead's phone,
 * which a form may have got wrong (owner, P38).
 */
export function whatsappPhoneOf(lead: {
  phone?: string | null;
  whatsappPhone?: string | null;
}): string | null {
  return lead.whatsappPhone ?? lead.phone ?? null;
}

export type ManualDelivery = {
  phone: string | null;
  /** Why the message leaves from the operator's WhatsApp instead of Polaris. */
  reason: "not_connected" | "window_closed";
  /** Messenger and Instagram (ADR 0092): the app to write from instead of WhatsApp. */
  app?: { channel: MessagingChannel; url: string | null };
};

/** Polaris can answer on Messenger and Instagram: the Page is connected and sends are on. */
export function canSendMessaging(workspace: Pick<WorkspaceData, "config" | "connections">) {
  return (
    workspace.config.sendsEnabled &&
    workspace.connections.some(
      (connection) => connection.kind === "meta_messaging" && connection.status === "active",
    )
  );
}

/**
 * How a reply to this lead leaves: null = from Polaris; otherwise copied or
 * opened in the operator's WhatsApp. Outside the 24-hour window Polaris could
 * send only an approved template, while the Business app can write freely.
 */
export function manualDeliveryFor(
  workspace: Pick<WorkspaceData, "config" | "connections">,
  lead: {
    phone?: string | null;
    whatsappPhone?: string | null;
    conversation?: {
      freeformWindowOpen: boolean;
      channel?: string;
      appUrl?: string | null;
    } | null;
  },
): ManualDelivery | null {
  const channel = lead.conversation?.channel;
  // Messenger and Instagram: after the window the text is copied into the app (ADR 0092).
  if (isMessagingChannel(channel)) {
    const app = { channel, url: lead.conversation?.appUrl ?? null };
    if (!canSendMessaging(workspace)) return { phone: null, reason: "not_connected", app };
    return lead.conversation?.freeformWindowOpen
      ? null
      : { phone: null, reason: "window_closed", app };
  }
  const phone = whatsappPhoneOf(lead);
  if (!canSendWhatsapp(workspace)) return { phone, reason: "not_connected" };
  if (!(lead.conversation?.freeformWindowOpen ?? false) && phoneAppAvailable(workspace)) {
    return { phone, reason: "window_closed" };
  }
  return null;
}

/** On a computer the operators write from the WhatsApp Desktop app; on a phone from the app. */
export function whatsappOpensDesktopApp(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
): boolean {
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
}

/**
 * The chat with the text ready: the WhatsApp Desktop app on a computer
 * (`whatsapp://send`, owner's choice of 2026-10-19 instead of WhatsApp Web),
 * the app on a phone (`wa.me`).
 */
export function whatsappDraftUrl(
  phone: string,
  text: string,
  desktop = whatsappOpensDesktopApp(),
): string | null {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return null;
  const encoded = encodeURIComponent(text);
  return desktop
    ? `whatsapp://send?phone=${digits}&text=${encoded}`
    : `https://wa.me/${digits}?text=${encoded}`;
}

/** The browser tab Polaris opens `wa.me` in. */
export const WHATSAPP_WINDOW = "polaris-whatsapp";

let handoffUntil = 0;

/**
 * True for a moment after Polaris handed a link to the desktop app: the browser
 * fires `beforeunload`, but the page is not being left.
 */
export function handingOffToWhatsapp(now = Date.now()): boolean {
  return now < handoffUntil;
}

/**
 * The desktop app link is handed to the app and Polaris stays where it is; a
 * `wa.me` link opens always in the same tab. A named tab needs the opener,
 * which is then cut.
 */
export function openWhatsappDraft(url: string): void {
  if (url.startsWith("whatsapp:")) {
    handoffUntil = Date.now() + 1_000;
    window.location.assign(url);
    return;
  }
  const opened = window.open(url, WHATSAPP_WINDOW);
  if (opened) opened.opener = null;
}

/**
 * The text as it may reach the lead from the operator's WhatsApp, through
 * "Copia" or "Apri in WhatsApp" (ADR 0063): {operatore} becomes the viewer's
 * first name; without it, or with any other placeholder left, there is no
 * text, and `message` says what to fix.
 */
export function phoneTextForLead(
  text: string,
  viewerName: string | null,
): { ok: true; text: string } | { ok: false; message: string } {
  const ready = textForLead(text.trim(), viewerName);
  if (ready.ok) return ready;
  return {
    ok: false,
    message:
      ready.code === "OPERATOR_NAME_MISSING"
        ? "Manca il tuo nome nel profilo: il messaggio uscirebbe con «{operatore}». Chiedi al titolare di aggiungerlo, o scrivi il nome nel testo."
        : `Il testo contiene «${ready.placeholder}», un segnaposto da compilare: correggilo prima di copiarlo o aprirlo in WhatsApp.`,
  };
}
